import { Middleware } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { Action, ExportFormat } from '../actions';
import { ActionType } from '../action-types';
import {
	addAudioFile,
	audioFileLoaded,
	deleteAudioFile,
	activateProject,
	setCurrentState,
	stopPlayback,
	updateTimelineTime,
} from '../action-creators';
import { delay, getBase64, saveFile } from '../../utils/general';
import { AudioPlaybackState } from '../../models/AudioPlayback';
import { audioBufferToWav, audioBufferToFlac } from '../../utils/audioExporter';
import { IProjectSaveFile, ISavedAudioFile } from '../../models/ProjectFile';
import { decode } from 'base64-arraybuffer';
import { startTimeline } from '../../utils/timelineHelper';
import { ReverbEffect } from '../../utils/reverbEffect';
import { getReverbSettingsForPreset } from '../../models/AcousticSettings';

declare global {
	interface Window {
		webkitAudioContext: typeof AudioContext;
	}
}

const audioPlaybackHandler: Middleware<{}, RootState> = (storeApi) => (next) => {
	const AudioContext = window.AudioContext || window.webkitAudioContext;
	const audioContext = new AudioContext();

	let activeRootNode: GainNode | null = null;
	let activeReverbEffect: ReverbEffect | null = null;
	let fadingOutRootNodes: GainNode[] = [];

	let nextAudioFileId = 1;
	const audioFilesById = new Map<number, File>();
	const audioBuffersById = new Map<number, AudioBuffer>();

	// Stop playing the current active audio
	async function stopActiveAudio() {
		if (activeRootNode === null) {
			return;
		}

		let rootNode = activeRootNode;
		fadingOutRootNodes.push(rootNode);
		activeRootNode = null;
		activeReverbEffect = null;

		// Fade out over a short amount of time so that it doesn't sound abrupt
		const fadeOutTime = 0.1;
		rootNode.gain.linearRampToValueAtTime(0.0, audioContext.currentTime + fadeOutTime);
		await delay(fadeOutTime * 1000);

		rootNode.disconnect();
		fadingOutRootNodes = fadingOutRootNodes.filter((node) => node !== rootNode);
		if (activeRootNode === null && fadingOutRootNodes.length === 0) {
			audioContext.suspend();
		}
	}

	let audioContextOffsetTime = 0;
	let timelineFrameUpdatesActive = false;

	// Sends timeline time updates every frame while the timeline is playing
	function onTimelineFrameUpdate(timeStamp: number) {
		const state = storeApi.getState();
		if (state.audioPlayback.state === AudioPlaybackState.PlayingTimeline) {
			const timelineTime = audioContext.currentTime - audioContextOffsetTime;
			const timelineDuration = state.undoable.timeline.duration;
			if (timelineTime <= timelineDuration) {
				storeApi.dispatch(updateTimelineTime(timelineTime));
			} else {
				storeApi.dispatch(stopPlayback());
				storeApi.dispatch(updateTimelineTime(timelineDuration));
			}
			window.requestAnimationFrame(onTimelineFrameUpdate);
		} else {
			timelineFrameUpdatesActive = false;
		}
	}

	function startTimelineFrameUpdates(audioContextTime: number, timelineTime: number) {
		audioContextOffsetTime = audioContextTime - timelineTime;
		if (!timelineFrameUpdatesActive) {
			timelineFrameUpdatesActive = true;
			window.requestAnimationFrame(onTimelineFrameUpdate);
		}
	}

	return async (action: Action) => {
		switch (action.type) {
			case ActionType.IMPORT_FILES: {
				// Add all the files to redux state so they show they are loading in the UI
				let newIds: number[] = [];
				for (const file of action.payload.files) {
					let id = nextAudioFileId++;

					// Read the file into a blob in memory so if the file changes on disk it won't affect it
					const blobFile = new File([new Blob([await file.arrayBuffer()])], file.name);

					audioFilesById.set(id, blobFile);
					newIds.push(id);
					storeApi.dispatch(addAudioFile(id, blobFile.name));
				}

				// Decode all the files to get an AudioBuffer and a duration
				for (const id of newIds) {
					let file = audioFilesById.get(id);
					if (file === undefined) continue;

					let audioBuffer: AudioBuffer;
					try {
						let audioFileData = await file.arrayBuffer();
						audioBuffer = await audioContext.decodeAudioData(audioFileData);
					} catch (e: any) {
						alert(
							"Could not decode audio file '" +
								file.name +
								"'.\n\nError: " +
								e.message
						);
						storeApi.dispatch(deleteAudioFile(id));
						continue;
					}

					audioBuffersById.set(id, audioBuffer);

					storeApi.dispatch(audioFileLoaded(id, audioBuffer.duration));
				}
				break;
			}
			case ActionType.DELETE_AUDIO_FILE: {
				if (action.payload.id === storeApi.getState().audioPlayback.audioFileId) {
					storeApi.dispatch(stopPlayback());
				}
				next(action);
				break;
			}
			case ActionType.PLAY_AUDIO_FILE: {
				let audioBuffer = audioBuffersById.get(action.payload.id);
				if (audioBuffer === undefined) {
					console.error('Trying to play audio file that has no existing audio buffer.');
					return;
				}

				stopActiveAudio();

				activeRootNode = audioContext.createGain();
				let sourceNode = audioContext.createBufferSource();
				sourceNode.buffer = audioBuffer;

				sourceNode.connect(activeRootNode).connect(audioContext.destination);
				sourceNode.start();

				audioContext.resume();
				next(action);
				break;
			}
			case ActionType.PLAY_TIMELINE: {
				stopActiveAudio();
				next(action);

				let rootNode = audioContext.createGain();
				let reverbEffect = await startTimeline(
					storeApi.getState(),
					audioContext,
					rootNode,
					action.payload.time,
					async (audioFileId) => audioBuffersById.get(audioFileId)
				);

				rootNode.connect(audioContext.destination);
				activeRootNode = rootNode;
				activeReverbEffect = reverbEffect;

				audioContext.resume();
				startTimelineFrameUpdates(audioContext.currentTime, action.payload.time);
				break;
			}
			case ActionType.EXPORT_TIMELINE: {
				const state = storeApi.getState();
				if (state.undoable.timeline.duration === 0) {
					alert('Nothing on timeline to export.');
					return;
				}

				next(action);

				let offlineAudioContext = new OfflineAudioContext({
					numberOfChannels: 2,
					length: state.undoable.timeline.duration * action.payload.sampleRate,
					sampleRate: action.payload.sampleRate,
				});

				let offlineAudioBuffersById = new Map<number, AudioBuffer>();
				await startTimeline(
					state,
					offlineAudioContext,
					offlineAudioContext.destination,
					0,
					async (audioFileId) => {
						let audioBuffer = offlineAudioBuffersById.get(audioFileId);
						if (audioBuffer !== undefined) return audioBuffer;

						let audioFile = audioFilesById.get(audioFileId);
						if (audioFile === undefined) return undefined;

						try {
							let audioFileData = await audioFile.arrayBuffer();
							audioBuffer = await audioContext.decodeAudioData(audioFileData);
						} catch (e: any) {
							alert(
								"Could not decode audio file for export '" +
									audioFile.name +
									"'.\n\nError: " +
									e.message
							);
							return undefined;
						}
						offlineAudioBuffersById.set(audioFileId, audioBuffer);
						return audioBuffer;
					}
				);

				let renderedBuffer = await offlineAudioContext.startRendering();

				let fileName = action.payload.fileName;
				if (fileName === '') fileName = 'export';

				switch (action.payload.format) {
					case ExportFormat.Wav: {
						let blob = audioBufferToWav(renderedBuffer);
						saveFile(blob, `${fileName}.wav`);
						break;
					}
					case ExportFormat.Flac: {
						let blob = audioBufferToFlac(renderedBuffer);
						if (blob instanceof Error) {
							alert('Failed to encode Flac file: ' + blob.message);
							return;
						}
						saveFile(blob, `${fileName}.flac`);
						break;
					}
				}

				break;
			}

			case ActionType.STOP_PLAYBACK: {
				stopActiveAudio();
				next(action);
				break;
			}
			case ActionType.SET_ACOUSTIC_SETTINGS_PRESET: {
				const state = storeApi.getState();
				if (activeReverbEffect !== null) {
					activeReverbEffect.update(
						getReverbSettingsForPreset(
							action.payload.preset,
							state.undoable.acousticSettings.reverbSettings
						)
					);
				}
				next(action);
				break;
			}
			case ActionType.SET_CUSTOM_ACOUSTIC_SETTINGS: {
				if (activeReverbEffect !== null) {
					activeReverbEffect.update(action.payload.reverbSettings);
				}
				next(action);
				break;
			}
			case ActionType.SET_TIMELINE_TIME: {
				const state = storeApi.getState();
				if (action.payload.time < 0) {
					next({ ...action, payload: { ...action.payload, time: 0 } });
				} else if (action.payload.time > state.undoable.timeline.duration) {
					next({
						...action,
						payload: { ...action.payload, time: state.undoable.timeline.duration },
					});
				} else {
					next(action);
				}

				if (
					storeApi.getState().audioPlayback.state === AudioPlaybackState.PlayingTimeline
				) {
					storeApi.dispatch(stopPlayback());
				}
				break;
			}
			case ActionType.SET_TRACK_MUTED:
			case ActionType.SET_ALL_TRACKS_MUTED:
			case ActionType.MOVE_AUDIO_CLIP:
			case ActionType.ADD_SET_PIECE:
			case ActionType.SET_SET_PIECE_RECT:
			case ActionType.ADD_DANCER_PATH_NODE:
			case ActionType.SET_DANCER_PATH_NODE:
			case ActionType.DELETE_DANCER_PATH_NODE: {
				next(action);
				if (
					storeApi.getState().audioPlayback.state === AudioPlaybackState.PlayingTimeline
				) {
					storeApi.dispatch(stopPlayback());
				}
				break;
			}
			case ActionType.LOAD_PROJECT: {
				const savedFile = action.payload.savedFile;

				// TO DO: Compare versions

				storeApi.dispatch(setCurrentState(savedFile.undoable));
				let newIds: number[] = [];
				savedFile.audiofiles.forEach((audioFile) => {
					const foundLoadedFile = savedFile.undoable.audioFiles.files.find(
						(file: any) => file.id === audioFile.id
					);

					const file = new File(
						[new Blob([decode(audioFile.fileData)])],
						foundLoadedFile
					);

					audioFilesById.set(audioFile.id, file);
					newIds.push(audioFile.id);
				});

				for (const id of newIds) {
					let file = audioFilesById.get(id);
					if (file === undefined) continue;

					let audioBuffer: AudioBuffer;
					try {
						let audioFileData = await file.arrayBuffer();
						audioBuffer = await audioContext.decodeAudioData(audioFileData);
					} catch (e: any) {
						alert(
							"Could not decode audio file '" +
								file.name +
								"'.\n\nError: " +
								e.message
						);
						storeApi.dispatch(deleteAudioFile(id));
						continue;
					}

					audioBuffersById.set(id, audioBuffer);

					storeApi.dispatch(audioFileLoaded(id, audioBuffer.duration));
					storeApi.dispatch(activateProject());
				}

				nextAudioFileId = savedFile.nextAudioFileId;

				break;
			}
			case ActionType.SAVE_PROJECT: {
				const result = prompt(
					'What would you like to name your project?',
					'Doppeltone_Project'
				);

				if (result) {
					const undoable = storeApi.getState().undoable;
					const audiofiles: ISavedAudioFile[] = [];

					for (let [id, file] of audioFilesById.entries()) {
						const base64 = await getBase64(file);

						audiofiles.push({
							id: id,
							fileData: base64,
						});
					}

					const savedFile: IProjectSaveFile = {
						version: '1',
						undoable,
						audiofiles,
						nextAudioFileId,
					};

					const blob = new Blob([JSON.stringify(savedFile)], {
						type: 'text/plain;charset=utf-8',
					});

					saveFile(blob, `${result.trim() !== '' ? result : 'Doppeltone_Project'}.dpl`);
				}

				break;
			}
			case ActionType.RESET_STATE: {
				if (!storeApi.getState().audioPlayback.projectActive) {
					storeApi.dispatch(activateProject());
					break;
				}

				const result = window.confirm(
					'Create new project? Current project changes will not be saved.'
				);

				if (result) {
					nextAudioFileId = 1;
					audioFilesById.clear();
					audioBuffersById.clear();

					next(action);
					storeApi.dispatch(activateProject());
				}
				break;
			}
			default: {
				next(action);
			}
		}
	};
};

export default audioPlaybackHandler;
